import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Modal } from 'components/Modal';
import { ModalBody } from 'components/Modal/ModalBody';
import { ModalActions } from 'components/Modal/ModalActions';
import { Button } from 'components/Button';
import { ClapIcon } from 'icons/outline';
import { HiringHeroBadgeGraphic } from 'graphics';
import { useHiringHeroMetric } from 'hooks/redux/useHiringHeroMetric';
import { AppRoutes } from 'util/urlFactory';

import type { HiringHeroSuccessModalProps } from './HiringHeroSuccessModal.types';

const HIRING_HERO_VIEWED_KEY = 'viewed_hiring_hero_benefits';

export const HiringHeroSuccessModal = (props: HiringHeroSuccessModalProps) => {
  const { debug } = props;
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const { loaded, enabled, isHiringHero } = useHiringHeroMetric();

  useEffect(() => {
    if (loaded && enabled) {
      if (!isHiringHero) {
        localStorage.removeItem(HIRING_HERO_VIEWED_KEY);
      } else {
        const dateViewedString = localStorage.getItem(HIRING_HERO_VIEWED_KEY);
        if (!dateViewedString) {
          setOpen(true);
        }
      }
    }
  }, [loaded, enabled]);

  const isInStatsPage = router.pathname === AppRoutes.EMPLOYER_HIRING_HERO_STATS_PAGE;

  const saveToLocalStorage = () => {
    const date = new Date();
    localStorage.setItem(HIRING_HERO_VIEWED_KEY, date.toString());
  };

  return (
    <Modal
      open={open}
      debug={debug}
      actionRequired={!isInStatsPage}
      onClose={() => {
        saveToLocalStorage();
        setOpen(false);
      }}
      heading={
        <div className="flex flex-col items-center gap-3">
          <HiringHeroBadgeGraphic className="w-12 h-14" />
          <h1 className="text-2xl font-bold text-neutral-ink-blue">
            Congrats! You're a&nbsp;
            <span className="border-b-4 border-primary-pure-blue">Hiring Hero</span>!
          </h1>
        </div>
      }
    >
      <ModalBody className="flex flex-col items-center gap-2.5">
        <Image
          alt="animating medal gif"
          width={225}
          height={225}
          src="/images/HiringHeroMedal.gif"
          unoptimized
        />
        <p className="text-xl text-center text-neutral-ink-blue">
          Now you'll attract more applicants, hire faster, gain visibility, and much more!
        </p>
      </ModalBody>
      <ModalActions className="!justify-center">
        {!isInStatsPage && (
          <Button
            color="poppy-red"
            size="lg"
            onClick={() => {
              saveToLocalStorage();
              router.push(AppRoutes.EMPLOYER_HIRING_HERO_STATS_PAGE);
            }}
          >
            <div className="!text-sm flex gap-2.5 items-center">
              <ClapIcon className="flex-none w-6 h-6" /> Benefits and Rewards
            </div>
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

HiringHeroSuccessModal.displayName = 'HiringHeroSuccessModal';
